import { css } from "@linaria/core";
import Button from "@ui/Button";
import React, { Fragment, memo, useMemo, useState } from "react";
import t from "../../../../../../../../../../i18n";
import { tokenize } from "@ui/lib/hooks/useFilteredDatas";
import Badge from "@ui/Badge";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import Text from "@ui/Text";
import * as Either from "fp-ts/Either";
import { colors, shadows, spacings } from "@ui/Theme";
import type { ResourceT } from "../../../../../../../schema/resource";
import { ContextualMenu, type ContextualMenuItem } from "./ContextualMenu";
import { useGUIEditorContext } from "../../../../../../../hook/context/gui";
import {
  GUIFaultColumns,
  GUIQuantityColumns,
  type GUIFaultColumnsT,
  type GUIQuantityColumnsT,
} from "../../../../../../../schema/gui";
import { ShareBoxLine } from "@ui/IconV2/react";
import { toPath } from "../../../../../../../../../../router/helpers/path";
import { toBasenamePath } from "../../../../../../../../../../router/helpers/navigation";
import { useAvailableResources } from "../../../../../../../hook/useAvailableResources";

const ResourceMenuItem = memo(
  ({
    identifier,
  }: {
    identifier:
      | { name: string; unit: string | null }
      | { name: string; system: string | null; displayName: string | null };
  }) => {
    const identifierName =
      "displayName" in identifier && typeof identifier.displayName === "string"
        ? identifier.displayName
        : identifier.name;

    return (
      <>
        <span className={resourceMenuItemIdentifier}>{identifierName}</span>
        {"unit" in identifier && identifier.unit && (
          <Badge backgroundColor={colors.carbon100} color={colors.carbon900}>
            {identifier.unit}
          </Badge>
        )}
        {"system" in identifier && identifier.system && (
          <Badge backgroundColor={colors.chlorine100} color={colors.carbon900}>
            {identifier.system}
          </Badge>
        )}
      </>
    );
  },
);

const columnOrder: (GUIQuantityColumnsT | GUIFaultColumnsT)[] = [
  "measured",
  "std",
  "expected",
  "gap",
  "normalizedGap",
  "real",
  "quantityImpacts",
  "globalImpact",
];

export const ResourceSelectorMenu = memo(
  ({
    projectId,
    onResourceSelect,
  }: {
    projectId: string;
    onResourceSelect: (value: ResourceT & { quantity?: string }) => void;
  }) => {
    const availableResources = useAvailableResources({ projectId });
    const { block } = useGUIEditorContext();
    const [search, setSearch] = useState("");

    const encodings = useMemo(
      () => block?.config.layers.flatMap(l => [l.encoding.x, l.encoding.y]) ?? [],
      [block?.config.layers],
    );

    const results = useMemo(
      () =>
        [availableResources.quantity, availableResources.fault].map(
          ({ kind, identifiers, columns }) => ({
            kind,
            identifiers: identifiers.filter(identifier =>
              tokenize(search).every(token => {
                const identifierName =
                  "displayName" in identifier && identifier.displayName
                    ? identifier.displayName
                    : identifier.name;

                return identifierName.toLowerCase().includes(token.toLowerCase());
              }),
            ),
            // missing observables.
            quantities: [
              ...availableResources.performanceIndicators,
              ...availableResources.observables,
            ],
            columns: columns.filter(column => {
              if (kind === "QUANTITY") return Either.isRight(GUIQuantityColumns.decode(column));
              if (kind === "FAULT") return Either.isRight(GUIFaultColumns.decode(column));
              return true;
            }),
          }),
        ),
      [
        availableResources.fault,
        availableResources.observables,
        availableResources.performanceIndicators,
        availableResources.quantity,
        search,
      ],
    );

    const menu: { kind: string; items: ContextualMenuItem[] }[] = useMemo(
      () =>
        results.map(({ kind, identifiers, columns, quantities }) => ({
          kind: t("project.dashboard.encoding.kind", kind),
          items: identifiers
            .map(identifier => {
              const identifierEncodings = encodings.filter(e => e.identifier === identifier.name);
              const isManualFailure = "manual" in identifier && identifier.manual === true;

              return {
                name: <ResourceMenuItem identifier={identifier} />,
                id: identifier.name,
                enabled: columns.some(c => identifierEncodings.every(e => e.column !== c)),
                items: (isManualFailure ? columns.filter(c => c === "quantityImpacts") : columns)
                  .map(c => ({
                    name: t("project.dashboard.resource.column", c),
                    id: c,
                    value: { kind, identifier: identifier.name, column: c },
                    enabled:
                      c === "quantityImpacts" || identifierEncodings.every(e => e.column !== c),
                    items:
                      c === "quantityImpacts"
                        ? (quantities
                            ?.map(q => ({
                              name: q?.name ?? "<unknown name>",
                              id: q?.name ?? "<unknown id>",
                              enabled: identifierEncodings.every(e => e.quantity !== q?.name),
                              value: {
                                kind,
                                identifier: identifier.name,
                                column: c,
                                quantity: q?.name,
                              },
                              items: [],
                            }))
                            .sort((a, b) => (a.id && b.id ? a.id.localeCompare(b.id) : 0)) ?? [])
                        : [],
                  }))
                  .sort((a, b) => {
                    const aIndex = columnOrder.indexOf(
                      a.id as GUIQuantityColumnsT | GUIFaultColumnsT,
                    );
                    const bIndex = columnOrder.indexOf(
                      b.id as GUIQuantityColumnsT | GUIFaultColumnsT,
                    );
                    return aIndex - bIndex;
                  }),
              };
            })
            .sort((a, b) => a.id.localeCompare(b.id)),
        })),
      [encodings, results],
    );

    return (
      <div className={resourceSelectorMenuDefault}>
        <div className={resourceSelectorMenuContainer}>
          <header>
            <SearchInputV2
              className={resourceSelectorMenuSearchInput}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </header>

          <div>
            {menu.map(
              ({ kind, items }) =>
                items.length > 0 && (
                  <Fragment key={kind}>
                    <Text className={resourceSelectorMenuGroupTitle}>{kind}</Text>
                    <ContextualMenu
                      items={items}
                      onClick={value =>
                        onResourceSelect(value as ResourceT & { quantity?: string })
                      }
                    />
                  </Fragment>
                ),
            )}
          </div>
          <hr />
          <footer>
            <Button
              variant="link"
              leftIcon={<ShareBoxLine />}
              slim
              className={resourceSelectorMenuCreateShortcut}
              onClick={() =>
                window.open(
                  toBasenamePath(
                    toPath("/projects/:projectId/settings/quantities", {
                      projectId,
                    }),
                  ),
                )
              }
            >
              {t("project.dashboard.resource.open_quantities")}
            </Button>
          </footer>
        </div>
      </div>
    );
  },
);

const resourceSelectorMenuCreateShortcut = css`
  display: block;
  margin: auto;
`;

const resourceSelectorMenuContainer = css`
  border-radius: 4px;
  overflow-y: auto;
  position: relative;
  max-height: 50vh;

  > :where(header, div, footer) {
    padding: ${spacings.s};
    background: ${colors.hydrogen};
  }

  > :where(header, footer) {
    position: sticky;
    z-index: 1;
  }

  > header {
    top: 0;
  }

  > footer {
    bottom: 0;
  }

  > hr {
    background: ${colors.titanium100};
    border: none;
    height: 1px;
    margin: 0 ${spacings.s};
  }
`;

const resourceSelectorMenuDefault = css`
  border-radius: 4px;
  box-shadow: ${shadows.shadow100};
  width: 350px;
`;

const resourceSelectorMenuSearchInput = css`
  width: 100%;
`;

const resourceSelectorMenuGroupTitle = css`
  color: ${colors.carbon700};
  padding: 10px 8px;
`;

const resourceMenuItemIdentifier = css`
  overflow: hidden;
  overflow-wrap: normal;
  text-overflow: ellipsis;
  width: 100%;
  display: block;
`;
